import React from "react";
import {
    Box,
    Card,
    CardBody,
    Flex,
    Heading,
    HStack,
    Icon,
    SimpleGrid,
    Text,
    VStack,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import type { IconType } from "react-icons";
import { PageHeader, type PageHeaderCrumb, type PageHeaderVariant } from "./PageHeader";

/** One clickable section surfaced on a hub page. */
export interface HubSection {
    /** Route the card links to. */
    to: string;
    /** Icon shown in the coloured square. */
    icon: IconType;
    /** Card title. */
    title: string;
    /** One-line description under the title. */
    description: string;
}

interface SectionHubProps {
    /** Banner identity forwarded to {@link PageHeader}. */
    variant?: PageHeaderVariant;
    /** Breadcrumb trail forwarded to {@link PageHeader}. */
    breadcrumbs: PageHeaderCrumb[];
    /** Page title forwarded to {@link PageHeader}. */
    title: string;
    /** Sections to list as cards. */
    sections: HubSection[];
    /** Background colour token of each card's icon square (section identity). */
    iconBg: string;
    /** Border colour token on card hover. */
    hoverBorder: string;
}

const HubCard = ({
    section,
    iconBg,
    hoverBorder,
}: Readonly<{ section: HubSection; iconBg: string; hoverBorder: string }>): JSX.Element => (
    <Card
        as={Link}
        to={section.to}
        variant="outline"
        transition="all 0.15s ease"
        _hover={{ shadow: "md", borderColor: hoverBorder, transform: "translateY(-2px)" }}
    >
        <CardBody>
            <HStack spacing={4} align="flex-start">
                <Flex
                    boxSize={10}
                    flexShrink={0}
                    align="center"
                    justify="center"
                    rounded="lg"
                    bg={iconBg}
                    color="white"
                >
                    <Icon as={section.icon} boxSize={5} />
                </Flex>
                <Box>
                    <Heading size="sm" mb={1}>
                        {section.title}
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                        {section.description}
                    </Text>
                </Box>
            </HStack>
        </CardBody>
    </Card>
);

/**
 * Reusable hub layout: a {@link PageHeader} banner over a responsive grid of
 * section cards. Shared by the Admin and Tools landing pages so the two hubs
 * never drift; each passes its own identity (variant + card colours).
 */
export const SectionHub = ({
    variant,
    breadcrumbs,
    title,
    sections,
    iconBg,
    hoverBorder,
}: SectionHubProps): JSX.Element => (
    <Box p={{ base: 4, md: 6 }}>
        <VStack spacing={6} align="stretch">
            <PageHeader variant={variant} breadcrumbs={breadcrumbs} title={title} />

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
                {sections.map((section) => (
                    <HubCard key={section.to} section={section} iconBg={iconBg} hoverBorder={hoverBorder} />
                ))}
            </SimpleGrid>
        </VStack>
    </Box>
);

export default SectionHub;
